import { useEffect, useState } from "react";
import { BACKEND_URL, authHeaders } from "./api";

// Media served by our own backend (GridFS icons/images/audio) now sits behind
// hikmah-backend's proxy, which needs a gateway Bearer token on every request.
// <img>/<audio> elements can't send that header (and the old httpOnly cookie
// isn't honoured by the gateway), so fetch with the token and hand the
// element a blob: URL instead. External/data:/blob: URLs pass through as-is.
const isOwn = (url) => typeof url === "string" && !!BACKEND_URL && url.startsWith(BACKEND_URL);

const cache = new Map(); // url -> Promise<blobUrl>; failures are evicted so a later sign-in can retry

export function fetchAuthedBlobUrl(url) {
    if (!cache.has(url)) {
        const p = fetch(url, { credentials: "include", headers: authHeaders() })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.blob();
            })
            .then((b) => URL.createObjectURL(b));
        p.catch(() => cache.delete(url));
        cache.set(url, p);
    }
    return cache.get(url);
}

export function useAuthedSrc(url) {
    const own = isOwn(url);
    const [state, setState] = useState({ src: own ? null : url || null, failed: false });
    useEffect(() => {
        if (!url || !own) { setState({ src: url || null, failed: false }); return undefined; }
        let cancelled = false;
        setState({ src: null, failed: false });
        fetchAuthedBlobUrl(url)
            .then((src) => { if (!cancelled) setState({ src, failed: false }); })
            .catch(() => { if (!cancelled) setState({ src: null, failed: true }); });
        return () => { cancelled = true; };
    }, [url, own]);
    return state;
}

export function AuthImg({ src, ...props }) {
    const { src: resolved } = useAuthedSrc(src);
    if (!resolved) return null;
    return <img src={resolved} {...props} />;
}

export function AuthAudio({ src, ...props }) {
    const { src: resolved } = useAuthedSrc(src);
    return <audio src={resolved || undefined} {...props} />;
}
